import { Card, Divider, Stack, SxProps, Typography } from "@mui/material";
import {
  ErrorPanel,
  formatDateTime,
  RelativeTime,
  useToast
} from "@qubit/autoparts";
import { useEffect } from "react";

import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  useGetAutostoreGridQuery,
  useGetAutostoreGridsQuery,
  useGetAutostoreGridStatusQuery,
  useGetAutostoreTasksQuery,
  useGetLogPublisherStateQuery
} from "~/redux/warehouse/autostoreGrid.hooks";

const GridInfoSx: SxProps = {
  p: 2,
  columnGap: 2,
  display: "grid",
  gridTemplateColumns: "0.6fr 0.7fr"
};

const GridInfo = ({ autostoreGridId }: { autostoreGridId: Guid }) => {
  const { data: autostoreGrid } = useGetAutostoreGridQuery(autostoreGridId);
  const {
    data: systemStatus,
    fulfilledTimeStamp,
    refetch: refetchGridStatus,
    error: gridStatusError
  } = useGetAutostoreGridStatusQuery(autostoreGridId, {
    pollingInterval: 5000
  });

  const {
    data: logPubData,
    isUninitialized: isLogPubUninitialized,
    isFetching: logPubLoading,
    refetch: refetchLogPub,
    error: logPubError
  } = useGetLogPublisherStateQuery(autostoreGridId, {
    pollingInterval: 60000
  });
  const {
    data: autostoreTasks,
    isUninitialized: isAutostoreTasksUninitialized,
    isFetching: autostoreTasksLoading,
    refetch: refetchAutostoreTasks,
    error: autostoreTasksError
  } = useGetAutostoreTasksQuery(autostoreGridId, {
    pollingInterval: 60000
  });

  const refetchAll = async () => {
    if (!isLogPubUninitialized) await refetchLogPub();
    if (!isAutostoreTasksUninitialized) await refetchAutostoreTasks();
  };
  const pickingTaskGroupCount =
    autostoreTasks?.filter((t) => t.category === 1) ?? [];
  const totalPickingTasks = pickingTaskGroupCount.reduce(
    (acc, curr) => acc + curr.tasks.length,
    0
  );
  const totalTasks =
    autostoreTasks?.reduce((acc, curr) => acc + curr.tasks.length, 0) ?? 0;
  const preparedBins = Object.values(logPubData?.binsByPos ?? {})
    .flatMap((b) => b)
    .filter((b) =>
      ["Prepared", "Closed", "Open", "Port", "To Grid", "Unknown"].includes(
        b.binMode
      )
    );
  const preparedTasks =
    autostoreTasks
      ?.flatMap((t) => t.tasks)
      .filter((t) => preparedBins.some((b) => b.binId === t.binId)) ?? [];

  if (!autostoreGrid) return <></>;

  return (
    <>
      <Card sx={GridInfoSx}>
        <Typography variant="h6" sx={{ gridColumn: "span 2" }}>
          {autostoreGrid?.autostoreGridName}
        </Typography>
        <Typography>Grid ID:</Typography>
        <Typography>{autostoreGridId}</Typography>
        {gridStatusError && (
          <ErrorPanel
            message={getMessageFromRtkError(gridStatusError)}
            tryAgain={{
              onTryAgain: async () => {
                await refetchGridStatus();
              }
            }}
          />
        )}
        {fulfilledTimeStamp && (
          <>
            <Typography>Last Refreshed:</Typography>
            <RelativeTime time={fulfilledTimeStamp} />
            <Typography gridColumn={2}>
              {formatDateTime(new Date(fulfilledTimeStamp))}
            </Typography>
          </>
        )}
        {systemStatus && (
          <>
            {systemStatus.kind === "connected" && (
              <>
                <Typography>Mode:</Typography>
                <Typography>{systemStatus.mode}</Typography>
                {systemStatus.stopCode && (
                  <>
                    <Typography>Stop Code:</Typography>
                    <Typography>{systemStatus.stopCode}</Typography>
                  </>
                )}
              </>
            )}
            {systemStatus.kind === "error" && (
              <>
                <Typography>Grid Error:</Typography>
                <Typography>{systemStatus.message}</Typography>
              </>
            )}
          </>
        )}
        {(logPubError || autostoreTasksError) &&
          !logPubLoading &&
          !autostoreTasksLoading && (
            <ErrorPanel
              message={getMessageFromRtkError(
                logPubError ?? autostoreTasksError
              )}
              tryAgain={{
                onTryAgain: refetchAll
              }}
            />
          )}
        {!logPubError &&
          !autostoreTasksError &&
          logPubData &&
          autostoreTasks && (
            <>
              <Divider sx={{ gridColumn: "span 2", my: 1 }} />
              <Typography>Total Tasks:</Typography>
              <Typography>{totalTasks}</Typography>
              <Typography>Picking Tasks:</Typography>
              <Typography>{totalPickingTasks}</Typography>
              <Typography>Picking Task Groups:</Typography>
              <Typography>{pickingTaskGroupCount.length}</Typography>
              <Typography>Prepared Bins:</Typography>
              <Typography>{preparedBins.length}</Typography>
              <Typography>Prepared Tasks:</Typography>
              <Typography>
                {preparedTasks.length} (
                {Math.trunc((preparedTasks.length / totalTasks) * 100)}%)
              </Typography>
            </>
          )}
      </Card>
    </>
  );
};

export const SystemStatus = () => {
  const { data: autostoreGrids, error } = useGetAutostoreGridsQuery();

  const { errorToast } = useToast();

  useEffect(() => {
    if (error) errorToast(getMessageFromRtkError(error));
  }, [error, errorToast]);

  return (
    <Stack flexDirection="row" gap={2}>
      {(autostoreGrids ?? []).map((g) => (
        <GridInfo key={g.autostoreGridId} autostoreGridId={g.autostoreGridId} />
      ))}
    </Stack>
  );
};
